/**
 * 支払準備の API 呼び出し。
 *
 * 実体は支払業務 GAS で、API はその口を許可リストつきで中継する。ここは画面から
 * その API を呼ぶだけで、実行できる処理の判定は API 側に任せる。
 *
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 支払管理レポートの一覧を読み、レポート作成・証憑整理のジョブを始めて状態を見に行き、リマインド送信・Old への移動を動かす
 * @graph-connects request [calls] ADO TASK API を呼ぶ
 */
import { request } from './client';

/** 支払管理レポート 1 件 */
export interface PaymentReportFile {
  /** Drive のファイル ID */
  id: string;
  /** ファイル名 */
  name: string;
  /** 開く URL */
  url: string;
  /** 最終更新（`YYYY-MM-DD HH:mm`） */
  updated: string;
}

/** 今月の証憑整理フォルダ 1 件 */
export interface PaymentFolder {
  /** Drive のフォルダ ID */
  id: string;
  /** フォルダ名 */
  name: string;
  /** 開く URL */
  url: string;
}

/** レポートとフォルダの一覧 */
export interface PaymentReportInfo {
  /** 支払管理レポート（新しい順） */
  reports: PaymentReportFile[];
  /** 今月の証憑整理フォルダ（新しい順） */
  folders: PaymentFolder[];
}

/** ジョブの状態 */
export interface PaymentJobState {
  /** 動かした処理 */
  fn: string;
  /** queued / running / done / error */
  status: string;
  /** いまの工程 */
  step: string;
  /** 件数の進み */
  current: number | null;
  /** 件数の全体 */
  total: number | null;
  /** 終わったときの結果 */
  result: Record<string, unknown> | null;
  /** 失敗の理由 */
  error: string;
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 支払管理レポートの一覧と今月の証憑整理フォルダを読む
 * @graph-connects request [calls] GET /payment-prep/report
 */
export function fetchPaymentReport(): Promise<PaymentReportInfo> {
  return request<PaymentReportInfo>('/payment-prep/report');
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business レポート作成・証憑整理をジョブとして始める
 * @graph-connects request [calls] POST /payment-prep/jobs
 */
export async function startPaymentJob(fn: string): Promise<PaymentJobState | null> {
  const body = await request<{ job: PaymentJobState | null }>('/payment-prep/jobs', {
    method: 'POST',
    body: JSON.stringify({ fn }),
  });
  return body.job;
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business いまのジョブの状態を読む
 * @graph-connects request [calls] GET /payment-prep/jobs
 */
export async function fetchPaymentJob(): Promise<PaymentJobState | null> {
  const body = await request<{ job: PaymentJobState | null }>('/payment-prep/jobs');
  return body.job;
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business リマインドの対象確認・送信と Old への移動を動かし、結果を返す
 * @graph-connects request [calls] POST /payment-prep/run
 */
export async function runPayment(fn: string, folderId?: string): Promise<unknown> {
  const body = await request<{ result: unknown }>('/payment-prep/run', {
    method: 'POST',
    body: JSON.stringify(folderId === undefined ? { fn } : { fn, folderId }),
  });
  return body.result;
}

/** 振込先口座が未登録の取引先 1 件 */
export interface MissingBankAccount {
  /** freee の取引先 ID */
  partnerId: number;
  /** 取引先名 */
  partnerName: string;
  /** 対象の取引の件数 */
  count: number;
  /** 対象の取引の金額の合計 */
  amount: number;
  /** 読み取りに使える証憑 ID */
  receiptIds: string[];
}

/** freee に登録する振込先口座（画面で人が直せる下書き） */
export interface BankAccountDraft {
  bankName: string;
  bankNameKana: string;
  bankCode: string;
  branchName: string;
  branchKana: string;
  branchCode: string;
  accountType: 'ordinary' | 'checking';
  accountNumber: string;
  accountName: string;
  longAccountName: string;
}

/** 請求書の読み取り結果 */
export interface BankAccountAnalysis {
  /** 登録の下書き */
  draft: BankAccountDraft;
  /** 確認が要る点 */
  warnings: string[];
  /** 読み取りの確からしさ（0〜1） */
  confidence: number;
  /** 確認用の請求書 */
  file: { name: string; mimeType: string; base64: string };
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 最新の支払管理レポートで振込先口座が未登録の取引先を読む
 * @graph-connects request [calls] GET /payment-prep/bank-accounts/missing
 */
export function fetchMissingBankAccounts(): Promise<{
  reportName: string;
  partners: MissingBankAccount[];
}> {
  return request('/payment-prep/bank-accounts/missing');
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 証憑の請求書を読み取って、振込先口座の下書きを作る（freee には書き込まない）
 * @graph-connects request [calls] POST /payment-prep/bank-accounts/analyze
 */
export function analyzeBankAccount(receiptId: string): Promise<BankAccountAnalysis> {
  return request('/payment-prep/bank-accounts/analyze', {
    method: 'POST',
    body: JSON.stringify({ receiptId }),
  });
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 人が確認した振込先口座を freee の取引先に登録する
 * @graph-connects request [calls] POST /payment-prep/bank-accounts
 */
export function registerBankAccount(
  partnerId: number,
  draft: BankAccountDraft,
): Promise<{ partnerId: number; partnerName: string }> {
  return request('/payment-prep/bank-accounts', {
    method: 'POST',
    body: JSON.stringify({ partnerId, ...draft }),
  });
}
